/**
 * R14b — 전역 지역 칩 「전국 ▾」 순수 규칙 한 자리 (NAV14_20260929 §R14b).
 *
 *   · 적용은 «URL» 로만 한다.
 *       부동산 문맥(/apt…)            → 색인 경로 /apt/region/{시도} · /apt/area/{시도}/{시군}  (HeaderRegionData 의 href 그대로)
 *       게시판·블로그·홈 목록          → 같은 경로 + ?r={regions.code}  (noindex,follow · canonical 은 파라미터 없는 경로)
 *       그 밖(주식·계산기·현장 상세 등) → 부동산 색인 경로(지역은 부동산 축이다)
 *   · 서버 HTML 은 언제나 「전국」. 현재 지역(경로·?r=)과 기억(localStorage)은 수화 «뒤» 에만 읽는다.
 *   · 기억은 칩 라벨과 `a[data-kd-region-link]`(탭·드롭다운의 지역 링크)만 바꾼다. 자동 이동 없음.
 *
 * ⛔ 지역 이름·개수 하드코딩 없음(G9) — 목록은 regions 표(HeaderRegionData)에서 온다.
 *    정렬은 현장 수(regions.site_count) 순 — 레인 E 에서 «부울경 3개 맨 앞» 하드코딩을 걷었다.
 */
import type { PickerLeaf, PickerTop } from '@/components/ds/v3/RegionPickerClient';
import type { HeaderRegionData } from '@/lib/v3/header-region-match';
import { matchHeaderRegion } from '@/lib/v3/header-region-match';

export const NATIONAL_LABEL = '전국';
/** ?r= 파라미터 이름. 게시판·블로그·홈 목록 필터 전용. */
export const REGION_PARAM = 'r';
/** 기억 키. 값 = {code}. 칩 라벨·지역 링크에만 쓴다. */
export const REGION_MEMORY_KEY = 'kd_nav_region_v1';

/** 선택된 지역 한 칸(시/도 또는 시·군). */
export interface ScopeNode {
  code: string;
  label: string;
  /** 부동산 색인 경로(허브). */
  href: string;
  /** 시·군이면 부모 시/도. */
  parent: { code: string; label: string; href: string } | null;
}

const CODE_RE = /^[0-9]{2,5}$/;

/** ?r= 값 검증 — 숫자 2~5자리 + 표에 있는 코드만. 그 밖은 null(= 전국). */
export function findScopeNode(data: HeaderRegionData | null | undefined, code: string | null | undefined): ScopeNode | null {
  const c = String(code ?? '').trim();
  if (!data || !CODE_RE.test(c)) return null;
  for (const t of data.tops) {
    if (t.code === c) return { code: t.code, label: t.label, href: t.href, parent: null };
    const k = t.children.find((x) => x.code === c);
    if (k) return { code: k.code, label: k.label, href: k.href, parent: { code: t.code, label: t.label, href: t.href } };
  }
  return null;
}

/** 칩 글자 — 「부산」 / 「경남 › 창원시」 / 「전국」. */
export function scopeLabel(node: ScopeNode | null): string {
  if (!node) return NATIONAL_LABEL;
  return node.parent ? `${node.parent.label} › ${node.label}` : node.label;
}

/**
 * 레인 E — 시·도 순서 = 현장 수(count) 많은 순, 같으면(또는 모르면) 표 순서. 수를 모르는 칸은 뒤.
 * 예전에는 「부울경 3개 맨 앞」 하드코딩이었다 — 전국 방문자에게 부산·울산·경남이 늘 먼저 보였다.
 * 방문자 «자기» 지역은 기억(kd_nav_region_v1)이 칩·경로로 먼저 보여 준다(이 정렬과 별개).
 * 레인 I — 현장 0곳 시·도는 숨기고, 2단(시·군·구)은 orderLeaves 로 같은 규칙(많은 순 · 0곳 숨김)을 적용한다.
 *   findScopeNode 는 이 결과가 아니라 원본 data.tops 를 본다 — 숨긴 칸의 기억·경로도 칩 라벨로는 그대로 풀린다.
 */
export function orderTops(tops: readonly PickerTop[]): PickerTop[] {
  return tops
    .filter((t) => t.count !== 0)
    .map((t, i) => ({ t, i }))
    .sort((a, b) => countOf(b.t) - countOf(a.t) || a.i - b.i)
    .map(({ t }) => (t.children.length ? { ...t, children: orderLeaves(t.children) } : t));
}

const countOf = (t: { count: number | null }) => (typeof t.count === 'number' && Number.isFinite(t.count) ? t.count : -1);

/**
 * 레인 I — 시·군·구(2단) 순서·숨김. 광역시·특별시 구·군도 regions 표 2단 노드로 들어왔다(지역 이름은 여기 없다 — G9).
 *   · 현장 0곳(count === 0)인 칸은 숨긴다 — 고르면 얇은 허브(308)로 떨어지는 선택지를 내놓지 않는다.
 *   · 수를 모르는 칸(null = 집계 전)은 숨기지 않는다(0 으로 위장 금지) — 뒤로 보낸다.
 *   · 현장 많은 순, 같으면 표 순서(행정코드 순).
 */
export function orderLeaves(leaves: readonly PickerLeaf[]): PickerLeaf[] {
  return leaves
    .filter((c) => c.count !== 0)
    .map((c, i) => ({ c, i }))
    .sort((a, b) => countOf(b.c) - countOf(a.c) || a.i - b.i)
    .map(({ c }) => c);
}

export interface RegionSearchHit { top: PickerTop; leaf: PickerLeaf | null }

/** 자동완성 — 시/도·시·군·구 이름 부분일치(오타 교정 없음) · 「시도+구」 붙여 쓰기 포함. 현장 많은 시·도 먼저(orderTops — 0곳 칸 제외). */
export function searchScope(data: HeaderRegionData | null | undefined, query: string, limit = 8): RegionSearchHit[] {
  const q = query.trim().replace(/\s+/g, '');
  if (!q || !data) return [];
  const out: RegionSearchHit[] = [];
  for (const t of orderTops(data.tops)) {
    const top = t.label.replace(/\s+/g, '');
    if (top.includes(q)) out.push({ top: t, leaf: null });
    // 레인 I — 「부산 남구」 처럼 시·도를 앞에 붙여 쳐도 잡는다(같은 이름의 구가 여러 시·도에 있다).
    for (const k of t.children) {
      const leaf = k.label.replace(/\s+/g, '');
      if (leaf.includes(q) || (top + leaf).includes(q)) out.push({ top: t, leaf: k });
    }
    if (out.length >= limit) break;
  }
  return out.slice(0, limit);
}

/* ─── 문맥 · 적용 URL ─────────────────────────────────────────────────────── */

export type ScopeContext = 'apt' | 'filter' | 'other';

const normPath = (p: string | null | undefined) => {
  let s = String(p ?? '/').split(/[?#]/)[0] || '/';
  try { s = decodeURIComponent(s); } catch { /* 원문 */ }
  return s.length > 1 ? s.replace(/\/+$/, '') : s;
};

/** 게시판 목록 경로인가 — /board · /board/{분류} · /board/{분류}/{정렬}. 스레드(숫자 시작)·글쓰기·신고·검색은 아니다. */
function isBoardList(p: string): boolean {
  if (p === '/board') return true;
  const m = p.match(/^\/board\/([^/]+)(?:\/([^/]+))?$/);
  if (!m) return false;
  if (['write', 'report', 'search', 'region', 'site'].includes(m[1])) return false;
  if (m[2] && /^\d/.test(m[2])) return false;
  return true;
}

/** 이 경로에서 칩을 누르면 무엇으로 적용되는가. */
export function scopeContext(pathname: string | null | undefined): ScopeContext {
  const p = normPath(pathname);
  if (p === '/apt' || p.startsWith('/apt/')) return 'apt';
  if (p === '/' || p === '/blog' || isBoardList(p)) return 'filter';
  return 'other';
}

/** ?r= 필터 목록의 «파라미터 없는» 기준 경로(= canonical 경로). 필터 문맥이 아니면 null. */
export function filterBasePath(pathname: string | null | undefined): string | null {
  const p = normPath(pathname);
  return scopeContext(p) === 'filter' ? p : null;
}

/**
 * 적용 URL.
 *   apt/other → 색인 허브(node.href) · 전국이면 /apt(부동산) — other 의 전국은 «이동 없음»(null).
 *   filter    → 기준 경로 + ?r=code · 전국이면 기준 경로(파라미터 제거). 다른 파라미터는 버린다(필터 결과 1쪽부터).
 * ⚠️ 경로의 한글은 encodeURI 로 다시 싼다 — 디코드된 경로를 그대로 push 하면 캐시 키가 갈린다.
 */
export function buildApplyHref(pathname: string | null | undefined, node: ScopeNode | null): string | null {
  const ctx = scopeContext(pathname);
  if (ctx === 'filter') {
    const base = encodeURI(filterBasePath(pathname) ?? '/');
    return node ? `${base}?${REGION_PARAM}=${encodeURIComponent(node.code)}` : base;
  }
  if (node) return node.href;
  return ctx === 'apt' ? '/apt' : null;
}

/**
 * 수화 뒤 칩이 보일 «현재» 지역. 우선순위: URL(경로 허브 · ?r=) > 기억 > 전국.
 * 서버 렌더는 이 함수를 부르지 않는다 — 서버 HTML 은 항상 「전국」.
 */
export function currentScope(
  data: HeaderRegionData | null | undefined,
  pathname: string | null | undefined,
  search: string | null | undefined,
  rememberedCode: string | null | undefined,
): { node: ScopeNode | null; source: 'path' | 'param' | 'memory' | 'national' } {
  const ctx = scopeContext(pathname);
  if (ctx === 'apt') {
    const trail = matchHeaderRegion(pathname, data);
    const last = trail[trail.length - 1];
    const node = last ? findScopeNode(data, last.code) : null;
    if (node) return { node, source: 'path' };
  }
  if (ctx === 'filter') {
    const r = readRegionParam(search);
    const node = findScopeNode(data, r);
    if (node) return { node, source: 'param' };
  }
  const mem = findScopeNode(data, rememberedCode);
  if (mem) return { node: mem, source: 'memory' };
  return { node: null, source: 'national' };
}

/** location.search → ?r= 원문(검증 전). */
export function readRegionParam(search: string | null | undefined): string | null {
  try {
    const v = new URLSearchParams(String(search ?? '')).get(REGION_PARAM);
    return v && v.trim() ? v.trim() : null;
  } catch { return null; }
}

/* ─── 색인 규칙 ───────────────────────────────────────────────────────────── */

/**
 * ?r= 가 붙은 필터 화면의 robots/canonical 규칙.
 *   · 유효한 지역이든 아니든 ?r= 가 «있으면» noindex,follow(파라미터 변형은 색인 대상이 아니다).
 *   · canonical 은 언제나 파라미터 없는 기준 경로(서버 메타가 이미 그렇게 낸다 — 여기선 확인만).
 */
export function filterRobots(pathname: string | null | undefined, search: string | null | undefined): { noindex: boolean; canonicalPath: string | null } {
  const base = filterBasePath(pathname);
  if (!base) return { noindex: false, canonicalPath: null };
  return { noindex: readRegionParam(search) !== null, canonicalPath: base };
}

/* ─── 기억(localStorage) — 수화 뒤에만 부른다 ──────────────────────────────── */

export function readRegionMemory(): string | null {
  return readRegionMemoryEntry()?.code ?? null;
}

/** nav_region1 — 기억 한 칸(코드 + 시·도 짧은 이름). sido 는 /calendar 지역 칩(시·도 이름 축) 기본값용 · 옛 기억엔 없다(null). */
export function readRegionMemoryEntry(): { code: string; sido: string | null; href: string | null; label: string | null } | null {
  try {
    if (typeof window === 'undefined') return null;
    const raw = window.localStorage.getItem(REGION_MEMORY_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as { code?: unknown; sido?: unknown; href?: unknown; label?: unknown };
    if (typeof v?.code !== 'string' || !CODE_RE.test(v.code)) return null;
    const str = (x: unknown, n: number) => (typeof x === 'string' && x.trim() ? x.trim().slice(0, n) : null);
    const href = str(v.href, 200);
    return { code: v.code, sido: str(v.sido, 20), href: href && /^\/apt\//.test(href) ? href : null, label: str(v.label, 40) };
  } catch { return null; }
}

/** node 를 주면 시·도 이름·허브 href·라벨을 함께 남긴다(/calendar 칩 기본값 · /apt 「최근 고른 지역」 띠). */
export function writeRegionMemory(code: string | null, node?: Pick<ScopeNode, 'label' | 'href' | 'parent'> | { sido: string } | null): void {
  try {
    if (typeof window === 'undefined') return;
    if (!code) window.localStorage.removeItem(REGION_MEMORY_KEY);
    else if (CODE_RE.test(code)) {
      const extra = !node ? {} : 'sido' in node ? { sido: node.sido }
        : { sido: node.parent?.label ?? node.label, href: node.href, label: scopeLabel(node as ScopeNode) };
      window.localStorage.setItem(REGION_MEMORY_KEY, JSON.stringify({ code, ...extra, ts: Date.now() }));
    }
  } catch { /* 저장 불가 환경 — 무시 */ }
}

/**
 * 탭·드롭다운의 «지역 링크» 목적지. 링크에 data-kd-region-link="apt|board|blog|lead" 를 단 것만 바꾼다(본문 링크 불변).
 *   apt   → 색인 허브   board → /board/region/{code}(색인 경로)   blog → /blog?r={code}
 */
export function regionLinkHref(kind: string | null | undefined, node: ScopeNode): string | null {
  if (kind === 'apt') return node.href;
  if (kind === 'board') return `/board/region/${encodeURIComponent(node.code)}`;
  if (kind === 'blog') return `/blog?${REGION_PARAM}=${encodeURIComponent(node.code)}`;
  if (kind === 'lead') return leadRegisterHrefFor(node);
  return null;
}

/**
 * retention-1031 — 「관심고객 등록」(nav-config LEAD_REGISTER_HREF)의 지역판. 리드폼은 시·도 허브에만 있으므로
 *   시·군이면 부모 시·도로 올린다. 허브 경로는 노드 href(/apt/region/…)를 쓰고, 모양이 다르면 이름으로 만든다.
 */
export function leadRegisterHrefFor(node: ScopeNode): string {
  const top = node.parent ?? node;
  const path = String(top.href ?? '').split(/[?#]/)[0];
  const base = path.startsWith('/apt/region/') && path.split('/').length === 4 ? path : `/apt/region/${encodeURIComponent(top.label)}`;
  return `${base}#lead-form`;
}

/* ─── nav_region1 — 적용 결과 계측(region_select_apply · result/target) ──────── */

export type ApplyResult = 'navigated' | 'filtered' | 'noop';

const decodeSafe = (s: string) => { try { return decodeURI(s); } catch { return s; } };

/**
 * 칩·바 적용 한 번의 결과. href = buildApplyHref 결과(null = 이동 없음), current = 지금 주소(pathname+search).
 *   select 모드(검색 시트) · href 없음 · 지금 주소와 같음 → noop
 *   필터 문맥(홈·블로그·게시판 목록 ?r=) → filtered · 그 밖 → navigated
 * target = 사람이 읽는 목적지(한글 디코드 — conversion_events 80자 열에 들어가게). noop 이면 지금 주소.
 */
export function applyOutcome(
  pathname: string | null | undefined,
  href: string | null,
  current: string | null | undefined,
  mode: 'navigate' | 'select' = 'navigate',
): { result: ApplyResult; target: string } {
  const cur = decodeSafe(String(current ?? pathname ?? '/'));
  if (mode === 'select' || !href) return { result: 'noop', target: cur };
  const t = decodeSafe(href);
  if (t === cur) return { result: 'noop', target: t };
  return { result: scopeContext(pathname) === 'filter' ? 'filtered' : 'navigated', target: t };
}
